"use client";

import { useEffect, useState } from "react";

import { postSkipWaiting, SW_URL } from "@/lib/pwa/sw-messages";

type SwRegistrationState = {
  registration: ServiceWorkerRegistration | null;
  hasUpdate: boolean;
  applyUpdate: () => void;
};

export function useSwRegistration(): SwRegistrationState {
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [hasUpdate, setHasUpdate] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!("serviceWorker" in navigator)) return;

    let cancelled = false;

    const trackWaiting = (reg: ServiceWorkerRegistration) => {
      if (reg.waiting && reg.active) setHasUpdate(true);
      reg.addEventListener("updatefound", () => {
        const installing = reg.installing;
        if (!installing) return;
        installing.addEventListener("statechange", () => {
          if (installing.state === "installed" && reg.active) setHasUpdate(true);
        });
      });
    };

    navigator.serviceWorker
      .getRegistration(SW_URL)
      .then((reg) => {
        if (cancelled || !reg) return;
        setRegistration(reg);
        trackWaiting(reg);
      })
      .catch(() => {
        // ignore
      });

    const onControllerChange = () => {
      // The new SW has taken control; reload so the page runs against the new version.
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);

    return () => {
      cancelled = true;
      navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
    };
  }, []);

  const applyUpdate = () => {
    if (!registration?.waiting) return;
    postSkipWaiting(registration.waiting);
  };

  return { registration, hasUpdate, applyUpdate };
}
